import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingCart,
  Package,
  Truck,
  User,
  Building,
  Calendar,
  FileText,
  Save,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  X,
  Check,
  XCircle,
  AlertCircle,
  MapPin,
  CreditCard
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  validateRequiredFields,
  validatePhoneInput,
  handleTextInputChange,
  getCharacterCount,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatDate, formatCurrency } from '../../../utils/stringUtils';
import quotationService from '../../../api/crm/quotations/quotationService';
import orderService from '../../../api/crm/orders/orderService';
import quotationTrackingService from '../../../api/crm/quotations/quotationTrackingService';
import apiClient from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/config';

const ConvertOrder = () => {
  const navigate = useNavigate();
  const { quoteId } = useParams();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('items');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // const [editingItemId, setEditingItemId] = useState(null);
  const [errors, setErrors] = useState({});
  const [quote, setQuote] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);

  // Order details state - will be auto-filled from quotation
  const [orderDetails, setOrderDetails] = useState({
    // orderNumber: '', // Manual input - no auto-generation
    customer: '',
    contact: '',
    email: '',
    phone: '',
    owner: '',
    billingAddress: {
      company: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      gstin: ''
    },
    shippingAddress: {
      company: '',
      address: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      contact: '',
      phone: '',
      deliveryInstructions: ''
    },
    deliveryType: 'Standard (5-7 days)',
    dispatchDate: '',
    // warehouse: '',
    notes: ''
  });

  const [orderItems, setOrderItems] = useState([]);


  // Fetch quotation data using React Query
  const {
    data: quotationResult,
    isLoading: loading,
    error: quotationError,
    refetch: refetchQuotation
  } = useQuery({
    queryKey: ['quotation', quoteId],
    queryFn: async () => {
      if (!quoteId) {
        throw new Error('Quote ID is required');
      }
      const result = await quotationService.getQuotationById(quoteId);

      if (!result.success || !result.quotation) {
        throw new Error(result.error || 'Failed to load quotation details');
      }

      const quotation = result.quotation;

      // Allow quotations with status "accepted" or "order" to be converted to orders
      const allowedStatuses = ['accepted', 'order'];
      if (!allowedStatuses.includes(quotation.status?.toLowerCase())) {
        throw new Error(`This quotation has status "${quotation.status || 'N/A'}". Only quotations with status "Accepted" or "Order" can be converted to orders.`);
      }

      return quotation;
    },
    enabled: !!quoteId,
    staleTime: 0,
    retry: 2,
  });

  // Fetch existing order if quotation has been converted
  const {
    data: existingOrderData,
    isLoading: loadingOrder
  } = useQuery({
    queryKey: ['order', quotationResult?.orderId],
    queryFn: async () => {
      if (!quotationResult?.orderId) return null;
      const orderResult = await orderService.getOrderById(quotationResult.orderId);
      if (orderResult.success && orderResult.order) {
        return orderResult.order;
      }
      return null;
    },
    enabled: !!quotationResult?.orderId && quotationResult?.status === 'converted',
    staleTime: 0,
    retry: 1,
  });

  // Update quote and existingOrder state when data is available
  useEffect(() => {
    if (quotationResult) {
      setQuote(quotationResult);
    }
  }, [quotationResult]);

  useEffect(() => {
    if (existingOrderData) {
      setExistingOrder(existingOrderData);
    } else if (quotationResult?.orderId && quotationResult?.status === 'converted') {
      // Set basic info if order fetch failed
      setExistingOrder({
        _id: quotationResult.orderId,
        orderNumber: 'N/A'
      });
    }
  }, [existingOrderData, quotationResult]);

  // Auto-fill order details when quotation data is available
  useEffect(() => {
    if (!quotationResult) return;

    const quotation = quotationResult;

    // Extract quotation-level addresses and related entities
    const quoteBillingAddress = quotation.billingAddress || {};
    const quoteShippingAddress = quotation.shippingAddress || {};

    // Check if quotation is from customer or opportunity
    const quotationCustomer = quotation.customer || null; // For customer-based quotations
    const lead = quotation.lead || {}; // For opportunity-based quotations
    const owner = quotation.createdBy || quotation.owner || {};

    // Create customer-like object - prioritize customer data, then lead data
    const customer = quotationCustomer ? {
      // Use customer data (for customer-based quotations)
      companyName: quotationCustomer.companyName || quoteBillingAddress.company || '',
      name: quotationCustomer.companyName || quoteBillingAddress.company || '',
      email: quotationCustomer.email || '',
      phone: (Array.isArray(quotationCustomer.phone) ? quotationCustomer.phone[0] : (quotationCustomer.phone || (Array.isArray(quoteShippingAddress.phone) ? quoteShippingAddress.phone[0] : (quoteShippingAddress.phone || '')))),
      contactPerson: quotationCustomer.contactPerson || '',
      gstin: quotationCustomer.gstin || quoteBillingAddress.gstin || '',
      address: quoteBillingAddress.street || quoteBillingAddress.address || '',
      city: quoteBillingAddress.city || '',
      state: quoteBillingAddress.state || '',
      pincode: quoteBillingAddress.pincode || '',
      billingAddress: {
        address: quoteBillingAddress.street || quoteBillingAddress.address || '',
        street: quoteBillingAddress.street || quoteBillingAddress.address || '',
        city: quoteBillingAddress.city || '',
        state: quoteBillingAddress.state || '',
        pincode: quoteBillingAddress.pincode || ''
      },
      shippingAddress: {
        address: quoteShippingAddress.street || quoteShippingAddress.address || '',
        street: quoteShippingAddress.street || quoteShippingAddress.address || '',
        city: quoteShippingAddress.city || '',
        state: quoteShippingAddress.state || '',
        pincode: quoteShippingAddress.pincode || ''
      }
    } : {
      // Use Lead data (for opportunity-based quotations)
      companyName: lead.company || quoteBillingAddress.company || '',
      name: lead.company || quoteBillingAddress.company || '',
      email: lead.email || '',
      phone: (Array.isArray(lead.phone) ? lead.phone[0] : (lead.phone || (Array.isArray(quoteShippingAddress.phone) ? quoteShippingAddress.phone[0] : (quoteShippingAddress.phone || '')))),
      contactPerson: lead.firstName && lead.lastName
        ? `${lead.firstName} ${lead.lastName}`.trim()
        : lead.firstName || lead.lastName || '',
      gstin: quoteBillingAddress.gstin || '',
      address: lead.address?.street || '',
      city: lead.address?.city || '',
      state: lead.address?.state || '',
      pincode: lead.address?.pincode || '',
      billingAddress: {
        address: lead.address?.street || '',
        street: lead.address?.street || '',
        city: lead.address?.city || '',
        state: lead.address?.state || '',
        pincode: lead.address?.pincode || ''
      },
      shippingAddress: {
        address: lead.address?.street || '',
        street: lead.address?.street || '',
        city: lead.address?.city || '',
        state: lead.address?.state || '',
        pincode: lead.address?.pincode || ''
      }
    };

    // Create contact-like object - prioritize customer data, then lead data
    const contactPerson = quotationCustomer ? {
      // Use customer data (for customer-based quotations)
      email: quotationCustomer.email || '',
      phone: (Array.isArray(quotationCustomer.phone) ? quotationCustomer.phone[0] : (quotationCustomer.phone || '')),
      firstName: quotationCustomer.contactPerson?.split(' ')[0] || '',
      lastName: quotationCustomer.contactPerson?.split(' ').slice(1).join(' ') || '',
      designation: ''
    } : {
      // Use Lead data (for opportunity-based quotations)
      email: lead.email || '',
      phone: lead.phone || '',
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      designation: lead.designation || ''
    };

    // Get contact person name - prioritize customer contactPerson, then lead name
    const contactName = quotationCustomer
      ? (quotationCustomer.contactPerson || 'N/A')
      : (contactPerson.firstName && contactPerson.lastName
        ? `${contactPerson.firstName} ${contactPerson.lastName}`.trim()
        : contactPerson.firstName || contactPerson.lastName || 'N/A');

    // Get customer name (company name)
    const customerName = customer.companyName || customer.name || 'N/A';

    // Get billing/shipping address from customer as fallback
    const customerBillingAddress = customer.billingAddress || {};
    const customerShippingAddress = customer.shippingAddress || {};

    // Generate order number from quote ID
    const quoteIdForOrder = quotation.quoteId || quotation.quoteNumber || quoteId;
    // const generatedOrderNumber = generateOrderNumberFromQuoteId(quoteIdForOrder);

    // Helper function to extract name from city/state/country object or string
    const extractName = (field) => {
      if (!field) return '';
      if (typeof field === 'string') return field;
      return field.name || field.id || '';
    };

    // Auto-fill order details
    setOrderDetails({
      // orderNumber: generatedOrderNumber, // Auto-generated from quote ID
      customer: customerName,
      contact: contactName,
      email: contactPerson.email || customer.email || '',
      phone: (Array.isArray(contactPerson.phone) ? contactPerson.phone[0] : (contactPerson.phone || (Array.isArray(customer.phone) ? customer.phone[0] : (customer.phone || '')))),
      owner: owner.firstName && owner.lastName
        ? `${owner.firstName} ${owner.lastName}`.trim()
        : owner.firstName || owner.lastName || owner.name || 'N/A',
      billingAddress: {
        company: quoteBillingAddress.company || customerName,
        address:
          quoteBillingAddress.street ||
          quoteBillingAddress.address ||
          customerBillingAddress.address ||
          customerBillingAddress.street ||
          customer.address ||
          '',
        city:
          extractName(quoteBillingAddress.city) ||
          extractName(customerBillingAddress.city) ||
          extractName(customer.city) ||
          '',
        state:
          extractName(quoteBillingAddress.state) ||
          extractName(customerBillingAddress.state) ||
          extractName(customer.state) ||
          '',
        country:
          extractName(quoteBillingAddress.country) ||
          extractName(customerBillingAddress.country) ||
          extractName(customer.country) ||
          '',
        pincode:
          quoteBillingAddress.pincode ||
          customerBillingAddress.pincode ||
          customer.pincode ||
          '',
        gstin: quoteBillingAddress.gstin || customer.gstin || customerBillingAddress.gstin || ''
      },
      shippingAddress: {
        company: quoteShippingAddress.company || customerName,
        address:
          quoteShippingAddress.street ||
          quoteShippingAddress.address ||
          customerShippingAddress.address ||
          customerShippingAddress.street ||
          customerBillingAddress.address ||
          customerBillingAddress.street ||
          customer.address ||
          '',
        landmark: quoteShippingAddress.landmark || '',
        city:
          extractName(quoteShippingAddress.city) ||
          extractName(customerShippingAddress.city) ||
          extractName(customerBillingAddress.city) ||
          extractName(customer.city) ||
          '',
        state:
          extractName(quoteShippingAddress.state) ||
          extractName(customerShippingAddress.state) ||
          extractName(customerBillingAddress.state) ||
          extractName(customer.state) ||
          '',
        country:
          extractName(quoteShippingAddress.country) ||
          extractName(customerShippingAddress.country) ||
          extractName(customerBillingAddress.country) ||
          extractName(customer.country) ||
          '',
        pincode:
          quoteShippingAddress.pincode ||
          customerShippingAddress.pincode ||
          customerBillingAddress.pincode ||
          customer.pincode ||
          '',
        contact: quoteShippingAddress.contact || contactName,
        phone: (Array.isArray(quoteShippingAddress.phone) ? quoteShippingAddress.phone[0] : (quoteShippingAddress.phone || (Array.isArray(contactPerson.phone) ? contactPerson.phone[0] : (contactPerson.phone || (Array.isArray(customer.phone) ? customer.phone[0] : (customer.phone || '')))))),
        deliveryInstructions: quoteShippingAddress.deliveryInstructions || ''
      },
      deliveryType: 'Standard (5-7 days)',
      dispatchDate: '',
      // warehouse: '',
      notes: ''
    });

    // Auto-fill order items from quotation items
    if (quotation.items && Array.isArray(quotation.items) && quotation.items.length > 0) {
      const formattedItems = quotation.items.map((item, index) => ({
        id: Date.now() + index + Math.random(),
        productName: item.productName || '',
        sku: item.sku || '',
        quantity: item.quantity || 0,
        mrp: item.mrp || 0,
        unitPrice: item.unitPrice || 0,
        expiry: item.expiry || null,
        manufacturing: item.manufacturing || '',
        discount: item.discount || 0,
        tax: item.tax || 0,
        lineTotal: item.lineTotal || 0, // Use lineTotal from backend
        priceOverride: item.priceOverride || false
      }));
      setOrderItems(formattedItems);
    }
  }, [quotationResult, quoteId]);


  const deliveryTypes = [
    'Standard (5-7 days)',
    'Express (2-3 days)',
    'Same Day',
    'Customer Pickup'
  ];

  // Format date as dd-mm-yyyy
  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Addresses are read-only in Convert to Order view; they are populated from the quotation.

  // Add Item functionality - REMOVED
  // const handleAddItem = () => {
  //   const newItem = {
  //     id: Date.now(),
  //     productName: '',
  //     sku: '',
  //     quantity: 1,
  //     unitPrice: 0,
  //     discount: 0,
  //     tax: 18,
  //     lineTotal: 0
  //   };
  //   setOrderItems([...orderItems, newItem]);
  //   setEditingItemId(newItem.id);
  // };

  // Edit Item functionality - REMOVED
  // const handleEditItem = (itemId) => {
  //   setEditingItemId(itemId);
  // };

  // const handleSaveEdit = () => {
  //   setEditingItemId(null);
  // };

  // const handleCancelEdit = () => {
  //   setEditingItemId(null);
  // };

  // const handleItemChange = (itemId, field, value) => {
  //   setOrderItems(orderItems.map(item => {
  //     if (item.id === itemId) {
  //       const updatedItem = { ...item, [field]: value };
  //       updatedItem.lineTotal = calculateLineTotal(updatedItem);
  //       return updatedItem;
  //     }
  //     return item;
  //   }));
  // };

  // const handleRemoveItem = (itemId) => {
  //   if (window.confirm('Are you sure you want to remove this item?')) {
  //     setOrderItems(orderItems.filter(item => item.id !== itemId));
  //     setEditingItemId(null);
  //   }
  // };

  const handleSaveDraft = () => {
    alert('Order saved as draft successfully!');
  };

  // Check if all required fields are filled
  const isAllRequiredFieldsFilled = () => {
    // Check order details
    // if (!orderDetails.orderNumber || !orderDetails.orderNumber.trim()) return false;
    if (!orderDetails.dispatchDate) return false;

    // Check billing address
    if (!orderDetails.billingAddress.company || !orderDetails.billingAddress.company.trim()) return false;
    if (!orderDetails.billingAddress.address || !orderDetails.billingAddress.address.trim()) return false;
    if (!orderDetails.billingAddress.city || !orderDetails.billingAddress.city.trim()) return false;
    if (!orderDetails.billingAddress.state || !orderDetails.billingAddress.state.trim()) return false;
    if (!orderDetails.billingAddress.pincode || !orderDetails.billingAddress.pincode.toString().trim()) return false;

    // Check shipping address
    if (!orderDetails.shippingAddress.company || !orderDetails.shippingAddress.company.trim()) return false;
    if (!orderDetails.shippingAddress.address || !orderDetails.shippingAddress.address.trim()) return false;
    if (!orderDetails.shippingAddress.city || !orderDetails.shippingAddress.city.trim()) return false;
    if (!orderDetails.shippingAddress.state || !orderDetails.shippingAddress.state.trim()) return false;
    if (!orderDetails.shippingAddress.pincode || !orderDetails.shippingAddress.pincode.toString().trim()) return false;
    const phoneStr = (orderDetails.shippingAddress.phone && Array.isArray(orderDetails.shippingAddress.phone)) ? (orderDetails.shippingAddress.phone[0] || '') : (orderDetails.shippingAddress.phone || '');
    if (!phoneStr || !phoneStr.toString().trim()) return false;

    // Check order items
    if (!orderItems || orderItems.length === 0) return false;

    // Check if all items have required fields
    for (const item of orderItems) {
      if (!item.productName || !item.productName.trim()) return false;
      if (!item.quantity || item.quantity <= 0) return false;
      if (item.unitPrice === undefined || item.unitPrice < 0) return false;
    }

    return true;
  };

  const handleConfirmOrder = () => {
    setShowConfirmModal(true);
  };

  // Update quotation status to "order" mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ quoteId, /* orderNumber, */ shippingAddress, plannedDispatchDate, deliveryType }) => {
      // First, update the quotation's shipping address with landmark and deliveryInstructions
      if (shippingAddress && (shippingAddress.landmark || shippingAddress.deliveryInstructions)) {
        // Get current quotation to preserve existing shipping address fields
        const currentQuoteResult = await quotationService.getQuotationById(quoteId);
        if (currentQuoteResult.success && currentQuoteResult.quotation) {
          const currentShippingAddress = currentQuoteResult.quotation.shippingAddress || {};

          // Normalize address field to street (backend schema uses 'street', not 'address')
          const normalizedAddress = currentShippingAddress.address || currentShippingAddress.street || '';

          // Update only landmark and deliveryInstructions, preserve all other fields
          const updatedShippingAddress = {
            company: currentShippingAddress.company || '',
            street: normalizedAddress, // Use 'street' as per schema
            landmark: shippingAddress.landmark || currentShippingAddress.landmark || '',
            city: currentShippingAddress.city || '',
            state: currentShippingAddress.state || '',
            pincode: currentShippingAddress.pincode || '',
            contact: currentShippingAddress.contact || '',
            phone: currentShippingAddress.phone || '',
            deliveryInstructions: shippingAddress.deliveryInstructions || currentShippingAddress.deliveryInstructions || ''
          };

          // Update quotation with new shipping address using apiClient directly with JSON
          try {
            const response = await apiClient.patch(
              `${API_ENDPOINTS.CRM.QUOTATIONS}/${quoteId}`,
              { shippingAddress: updatedShippingAddress },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );

            if (response.responseCode !== 200) {
              throw new Error(response.responseMessage || 'Failed to update shipping address');
            }
          } catch (error) {
            throw new Error(error.response?.data?.responseMessage || error.message || 'Failed to update shipping address');
          }
        }
      }

      // Then update quotation status to "order" with dispatch date and delivery type
      const result = await quotationTrackingService.updateQuoteStatus(
        quoteId,
        'order',
        /* orderNumber, */
        null, // orderNumber is null
        plannedDispatchDate,
        deliveryType
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to update quotation status');
      }
      return result;
    },
    onSuccess: async () => {
      // Invalidate and refetch quotation to get updated status
      await queryClient.invalidateQueries({ queryKey: ['quotation', quoteId] });
      await refetchQuotation();

      // Close modal
      setShowConfirmModal(false);

      // Show success message
      const tempDiv = document.createElement('div');
      tempDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg z-50';
      tempDiv.textContent = 'Quotation status updated to Order successfully!';
      document.body.appendChild(tempDiv);

      setTimeout(() => {
        if (document.body.contains(tempDiv)) {
          document.body.removeChild(tempDiv);
        }
        // Navigate back to quotation listing
        navigate('/crm/quotation-proposal/create-quotes');
      }, 2000);
    },
    onError: (error) => {
      const errorMessage = error.message || 'Failed to update quotation status. Please try again.';
      alert(errorMessage);
    },
  });

  // Handle confirm order - update quotation status to "order"
  const handleConfirmOrderFinal = async () => {
    if (!quoteId) {
      alert('Invalid quotation ID');
      return;
    }

    // Get the order number from orderDetails
    // const orderNumber = orderDetails.orderNumber;
    // if (!orderNumber || !orderNumber.trim()) {
    //   alert('Order number is required');
    //   return;
    // }

    // Get shipping address with landmark and deliveryInstructions from form
    const shippingAddress = {
      landmark: orderDetails.shippingAddress.landmark || '',
      deliveryInstructions: orderDetails.shippingAddress.deliveryInstructions || ''
    };

    // Get dispatch date and delivery type
    const plannedDispatchDate = orderDetails.dispatchDate || null;
    const deliveryType = orderDetails.deliveryType || null;

    // Update quotation status to "order" and send order number, shipping address, dispatch date, and delivery type
    updateStatusMutation.mutate({
      quoteId,
      /* orderNumber, */
      shippingAddress,
      plannedDispatchDate,
      deliveryType
    });
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/crm/quotation-proposal/dashboard');
    }
  };

  // Use totals from backend (quotation data) - do not calculate
  const subtotal = quotationResult?.subtotal || 0;
  const discountAmount = quotationResult?.discountAmount || 0;
  const taxAmount = quotationResult?.taxAmount || 0;
  const totalAmount = quotationResult?.totalAmount || 0;
  const taxableAmount = subtotal - discountAmount;

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Convert to Order</h1>
            <p className="text-gray-600">Loading quotation details...</p>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-12">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-200 rounded w-48 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
            <div className="space-y-3 mt-8">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  const error = quotationError?.message || quotationError || null;
  if (error || (!loading && !quotationResult)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Convert to Order</h1>
            <p className="text-gray-600">Quote ID: {quoteId}</p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Quotation</h3>
              <p className="text-sm text-red-700 mt-1">{error || 'Quotation not found'}</p>
              <Button
                variant="outline"
                onClick={() => navigate('/crm/quotation-proposal/convert-order')}
                className="mt-4"
              >
                Go Back to Listing
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Ensure quotationResult exists before proceeding
  if (!quotationResult) {
    return null;
  }

  // Check if order already exists
  const isOrderAlreadyCreated = (quotationResult?.status === 'converted' && quotationResult?.orderId) || existingOrder;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Convert to Order</h1>
            <p className="text-gray-600">Quote ID: {quote?.quoteNumber || quote?.quoteId || quoteId}</p>
          </div>
        </div>
      </div>

      {/* Order Already Created Message */}
      {isOrderAlreadyCreated && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-yellow-800 mb-2">
                Order Already Created for This Quotation
              </h2>
              <p className="text-yellow-700 mb-4">
                This quotation has already been converted to an order. You cannot create a duplicate order.
              </p>
              {existingOrder && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-yellow-800">
                    Order Number: <span className="font-bold">{existingOrder.orderNumber || 'N/A'}</span>
                  </p>
                  {existingOrder.orderId && (
                    <p className="text-sm text-yellow-700 mt-1">
                      Order ID: {existingOrder.orderId}
                    </p>
                  )}
                </div>
              )}
              {quote?.orderId && (
                <PermissionGate permission="order_management" action="read">
                  <Button
                    variant="primary"
                    onClick={() => navigate(`/crm/orders/${quote.orderId}`)}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  >
                    View Order
                  </Button>
                </PermissionGate>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quote Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Accepted Quote Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-start space-x-3 min-w-0">
            <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-600 mb-1">Company Name</p>
              <p className="font-medium text-gray-900 break-words capitalize">
                {orderDetails.customer || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-600 mb-1">Customer Name</p>
              <p className="font-medium text-gray-900 break-words capitalize">
                {orderDetails.contact || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Package className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-600 mb-1">Quote Value</p>
              <p className="font-medium text-gray-900 whitespace-nowrap">
                ₹{((quote?.totalAmount || quote?.value || 0)).toLocaleString('en-IN')}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3 min-w-0">
            <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-600 mb-1">Accepted Date</p>
              <p className="font-medium text-gray-900">
                {quote?.acceptedDate
                  ? new Date(quote.acceptedDate).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dispatch Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={orderDetails.dispatchDate}
              min={new Date().toISOString().split('T')[0]} // Prevent past dates
              onClick={(e) => e.target.showPicker()}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split('T')[0];

                // Validate that selected date is not in the past
                if (selectedDate && selectedDate < today) {
                  setErrors(prev => ({
                    ...prev,
                    dispatchDate: 'Dispatch date cannot be in the past'
                  }));
                  return;
                }

                setOrderDetails({ ...orderDetails, dispatchDate: selectedDate });
                if (errors.dispatchDate) {
                  setErrors(prev => ({ ...prev, dispatchDate: '' }));
                }
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${errors.dispatchDate ? 'border-red-300' : 'border-gray-300'
                }`}
              required
            />
            {errors.dispatchDate && (
              <p className="text-xs text-red-600 mt-1">{errors.dispatchDate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Type
            </label>
            <select
              value={orderDetails.deliveryType}
              onChange={(e) => setOrderDetails({ ...orderDetails, deliveryType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
            >
              {deliveryTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'items', label: 'Order Items', icon: Package },
              { key: 'billing', label: 'Billing Address', icon: Building },
              { key: 'shipping', label: 'Shipping Address', icon: Truck }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.key
                  ? 'border-agrosaf-green text-agrosaf-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Order Items Tab */}
          {activeTab === 'items' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Order Items</h3>
                {/* Add Item button removed */}
                {/* <Button
                  variant="outline"
                  size="sm"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={handleAddItem}
                >
                  Add Item
                </Button> */}
              </div>

              <div className="overflow-x-auto -mx-6 px-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MRP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacturer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orderItems.map((item, index) => (
                      <tr key={item.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 break-words text-sm font-medium text-gray-900 capitalize">
                          {item.productName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">{item.quantity || 0}</td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">₹{(item.mrp || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">
                          ₹{item.unitPrice?.toLocaleString('en-IN') || '0'}
                          {item.priceOverride && <span className="text-red-600 text-xs ml-1">(Override)</span>}
                        </td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">{formatDateDDMMYYYY(item.expiry)}</td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">{item.manufacturing || '-'}</td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">{item.discount || 0}%</td>
                        <td className="px-4 py-3 break-words text-sm text-gray-600">{item.tax || 0}%</td>
                        <td className="px-4 py-3 break-words text-sm font-medium text-gray-900">₹{item.lineTotal?.toLocaleString('en-IN') || '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-80 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">₹{subtotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Discount:</span>
                    <span className="font-medium text-green-600">-₹{discountAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Taxable Amount:</span>
                    <span className="font-medium">₹{taxableAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Tax:</span>
                    <span className="font-medium">₹{taxAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2">
                    <span className="text-lg font-semibold text-gray-900">Grand Total:</span>
                    <span className="text-lg font-bold text-agrosaf-green">₹{totalAmount.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Billing Address Tab - read only, two column layout */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              {orderDetails.billingAddress ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 min-w-0">
                    <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.billingAddress.company || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">House no. / Street</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.billingAddress.address || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">City</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.billingAddress.city || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">State</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.billingAddress.state || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Pincode</p>
                      <p className="font-medium text-gray-900 break-words">
                        {orderDetails.billingAddress.pincode || 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Country</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.billingAddress.country || 'N/A'}
                      </p>
                    </div>
                  </div>
                  {orderDetails.billingAddress.gstin && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <CreditCard className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">GSTIN</p>
                        <p className="font-medium text-gray-900 break-words uppercase">
                          {orderDetails.billingAddress.gstin}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No billing address available</p>
              )}
            </div>
          )}

          {/* Shipping Address Tab - read only, landmark & delivery instructions editable */}
          {activeTab === 'shipping' && (
            <div className="space-y-6">
              {orderDetails.shippingAddress || orderDetails.billingAddress ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3 min-w-0">
                    <Building className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.shippingAddress.company ||
                          orderDetails.billingAddress.company ||
                          orderDetails.customer ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">House no. / Street</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.shippingAddress.address ||
                          orderDetails.billingAddress.address ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">City</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.shippingAddress.city ||
                          orderDetails.billingAddress.city ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">State</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.shippingAddress.state ||
                          orderDetails.billingAddress.state ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Pincode</p>
                      <p className="font-medium text-gray-900 break-words">
                        {orderDetails.shippingAddress.pincode ||
                          orderDetails.billingAddress.pincode ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600">Country</p>
                      <p className="font-medium text-gray-900 break-words capitalize">
                        {orderDetails.shippingAddress.country ||
                          orderDetails.billingAddress.country ||
                          'N/A'}
                      </p>
                    </div>
                  </div>
                  {orderDetails.shippingAddress.contact && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">Contact Person</p>
                        <p className="font-medium text-gray-900 break-words capitalize">
                          {orderDetails.shippingAddress.contact || orderDetails.contact || 'N/A'}
                        </p>
                      </div>
                    </div>
                  )}
                  {orderDetails.shippingAddress.phone && (
                    <div className="flex items-start space-x-3 min-w-0">
                      <User className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900 break-words">
                          {orderDetails.shippingAddress.phone || orderDetails.phone || 'N/A'}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start space-x-3 min-w-0">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm text-gray-600 mb-1">
                        Landmark
                      </label>
                      <div className="space-y-1">
                        <input
                          type="text"
                          value={orderDetails.shippingAddress.landmark || ''}
                          onChange={(e) => {
                            const sanitized = handleTextInputChange(e, null, 100);
                            setOrderDetails({
                              ...orderDetails,
                              shippingAddress: {
                                ...orderDetails.shippingAddress,
                                landmark: sanitized
                              }
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          placeholder="Near landmark, building name, etc."
                          maxLength={100}
                        />
                        <div className="text-right text-xs text-gray-500">
                          {getCharacterCount(orderDetails.shippingAddress.landmark || '', 100)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 min-w-0 md:col-span-2">
                    <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <label className="block text-sm text-gray-600 mb-1">
                        Delivery Instructions
                      </label>
                      <div className="space-y-1">
                        <textarea
                          rows={5}
                          value={orderDetails.shippingAddress.deliveryInstructions || ''}
                          onChange={(e) => {
                            const sanitized = handleTextInputChange(e, null, 500);
                            setOrderDetails({
                              ...orderDetails,
                              shippingAddress: {
                                ...orderDetails.shippingAddress,
                                deliveryInstructions: sanitized
                              }
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          placeholder="Special delivery instructions, timing preferences, etc."
                          maxLength={500}
                        />
                        <div className="text-right text-xs text-gray-500">
                          {getCharacterCount(orderDetails.shippingAddress.deliveryInstructions || '', 500)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No shipping address available</p>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Confirm Order Button - Bottom Centered */}
      {!isOrderAlreadyCreated && (
        <div className="flex justify-center">
          <PermissionGate permission="order_management" action="create">
            <Button
              variant="primary"
              icon={<CheckCircle className="h-4 w-4" />}
              onClick={handleConfirmOrder}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              disabled={updateStatusMutation.isPending || !orderDetails.dispatchDate}
            >
              {updateStatusMutation.isPending ? 'Updating...' : 'Confirm Order'}
            </Button>
          </PermissionGate>
        </div>
      )}

      {/* Confirm Order Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Order</h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm text-yellow-800">
                      Please review all order details before confirming. Once confirmed, the order will be sent to the logistics team for processing.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Order Summary</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    {/* <p><strong>Order Number:</strong> {orderDetails.orderNumber}</p> */}
                    <p className="capitalize"><strong>Customer:</strong> <span className="break-words">{orderDetails.customer}</span></p>
                    <p><strong>Total Value:</strong> {formatCurrency(totalAmount)}</p>
                    <p><strong>Items:</strong> {orderItems.length}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Delivery Details</h4>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Delivery Type:</strong> {orderDetails.deliveryType}</p>
                    <p>
                      <strong>Dispatch Date:</strong>{' '}
                      {orderDetails.dispatchDate ? formatDate(orderDetails.dispatchDate, { includeTime: false }) : 'Not set'}
                    </p>
                    {/* <p><strong>Warehouse:</strong> {orderDetails.warehouse}</p> */}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </Button>
              <PermissionGate permission="order_management" action="create">
                <Button
                  variant="primary"
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                  onClick={handleConfirmOrderFinal}
                  disabled={updateStatusMutation.isPending}
                >
                  {updateStatusMutation.isPending ? 'Updating...' : 'Confirm Order'}
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConvertOrder;
